import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, Syringe, Zap, Heart, Clock, ChevronDown, ChevronUp, Plus, Check, Droplets, ArrowRight, Crown, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { TREATMENT_CATEGORY_CARDS, TreatmentCategoryCard } from "@/components/TreatmentCategoriesGrid";

const MENU_BUCKETS = [
  ["botox", "Botox", "Botox", "bg-rose-500/20 text-rose-200 border-rose-500/30"],
  ["fillers", "Fillers", "Comblement", "bg-purple-500/20 text-purple-200 border-purple-500/30"],
  ["sculptra_boosters", "Sculptra & Boosters", "Sculptra & Boosters", "bg-teal-500/20 text-teal-200 border-teal-500/30"],
  ["microneedling", "Microneedling", "Micro-aiguilles", "bg-sky-500/20 text-sky-200 border-sky-500/30"],
  ["facials_peels", "Facials & Peels", "Soins & Peelings", "bg-emerald-500/20 text-emerald-200 border-emerald-500/30"],
  ["laser_ipl", "Laser & IPL", "Laser & IPL", "bg-amber-500/20 text-amber-200 border-amber-500/30"],
  ["prp", "PRP", "PRP", "bg-red-500/20 text-red-200 border-red-500/30"],
  ["consultations", "Free consults", "Consultations", "bg-white/10 text-white border-white/20"],
];

const FullMenuBanner = ({ isFr }) => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/square/catalog-summary`, { timeout: 45000 })
      .then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, []);
  const counts = summary?.counts || {};
  return (
    <div className="mb-12">
      <Link to="/book/menu" onClick={() => window.scrollTo(0, 0)} className="block group" data-testid="full-menu-banner">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 p-1">
          <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 rounded-xl p-6 md:p-8 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-400 rounded-full blur-3xl"></div>
            </div>
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 md:gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <div>
                  <span className="text-amber-400 text-xs tracking-widest uppercase" data-testid="full-menu-total">
                    {summary ? (isFr ? `${summary.total} soins · 3 cliniques` : `${summary.total} treatments · 3 clinics`) : (isFr ? "Menu en direct" : "Live menu")}
                  </span>
                  <h3 className="text-xl md:text-2xl lg:text-3xl text-white font-semibold mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isFr ? "Le Menu " : "The Full "}<span className="italic text-amber-400">{isFr ? "Complet" : "Menu"}</span>
                  </h3>
                  <p className="text-gray-400 text-sm mt-1 max-w-md">
                    {isFr ? "Promotions d'abord · tapez un soin → choisissez une heure" : "Promotions first · tap a treatment → pick a time"}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg transition-all group-hover:gap-4 shadow-lg">
                  <span style={{ fontFamily: "'Playfair Display', serif" }}>{isFr ? "Voir tous les soins" : "See all treatments"}</span>
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </div>
            {summary && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-5 pt-4 border-t border-gray-700" data-testid="full-menu-counts">
                {MENU_BUCKETS.filter(([k]) => counts[k] > 0).map(([k, en, fr, cls]) => (
                  <span key={k} className={`px-2.5 py-1 text-[11px] font-semibold rounded border ${cls}`} data-testid={`full-menu-count-${k}`}>
                    {counts[k]} {isFr ? fr : en}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

const categoryIcons = {
  "Neuromodulator - Botox": Syringe,
  "Filler": Sparkles,
  "Sculptra": Heart,
  "Medical Micro Needling": Zap,
  "PRP Therapy": Heart,
  "Products": Sparkles,
  "Consultation": Heart,
};

// Translation maps for categories
const categoryTranslations = {
  "Neuromodulator - Botox": { en: "Neuromodulator - Botox", fr: "Neuromodulateur - Botox" },
  "Filler": { en: "Filler", fr: "Agent de Comblement" },
  "Sculptra": { en: "Sculptra", fr: "Sculptra" },
  "Medical Micro Needling": { en: "Medical Micro Needling", fr: "Micro-Aiguilles Médicales" },
  "PRP Therapy": { en: "PRP Therapy", fr: "Thérapie PRP" },
  "Products": { en: "Products", fr: "Produits" },
  "Consultation": { en: "Consultation", fr: "Consultation" },
};

// Translation maps for service names
const serviceNameTranslations = {
  // Neuromodulator - Botox
  "The Big 3 - Crow's Feet, 11's, Forehead": { en: "The Big 3 - Crow's Feet, 11's, Forehead", fr: "Les 3 Grandes Zones - Pattes d'Oie, 11's, Front" },
  "Forehead": { en: "Forehead", fr: "Front" },
  "Number 11's": { en: "Number 11's", fr: "Numéro 11's" },
  "The Forehead + 11's (PROMO)": { en: "The Forehead + 11's (PROMO)", fr: "Front + 11's (PROMO)" },
  "Migraine Headaches Treatment": { en: "Migraine Headaches Treatment", fr: "Traitement des Migraines" },
  "TMJ-Masseters/Bruxism/Jaw Grinding": { en: "TMJ-Masseters/Bruxism/Jaw Grinding", fr: "ATM-Masséters/Bruxisme/Grincement" },
  "Hyperhidrosis": { en: "Hyperhidrosis", fr: "Hyperhidrose" },
  "Neck Bands Platysmal": { en: "Neck Bands Platysmal", fr: "Bandes du Cou Platysmal" },
  "Nefertiti Jaw and Neck Lift": { en: "Nefertiti Jaw and Neck Lift", fr: "Lifting Néfertiti Mâchoire et Cou" },
  "Smile Lift": { en: "Smile Lift", fr: "Lifting du Sourire" },
  "The Gummy Smile": { en: "The Gummy Smile", fr: "Le Sourire Gingival" },
  "Lip Flip": { en: "Lip Flip", fr: "Lip Flip" },
  "Jelly Roll (Under Eyes)": { en: "Jelly Roll (Under Eyes)", fr: "Jelly Roll (Sous les Yeux)" },
  "MicroTox Neck": { en: "MicroTox Neck", fr: "MicroTox Cou" },
  "MicroTox Full Face and Neck": { en: "MicroTox Full Face and Neck", fr: "MicroTox Visage Complet et Cou" },
  "Trapezoids (Traptox)": { en: "Trapezoids (Traptox)", fr: "Trapèzes (Traptox)" },
  "Brow Lift": { en: "Brow Lift", fr: "Lifting des Sourcils" },
  "Bunny Lines": { en: "Bunny Lines", fr: "Rides du Nez" },
  "Full Upper Face Package": { en: "Full Upper Face Package", fr: "Forfait Visage Supérieur Complet" },
  "PROMO: 11's + Brow Lift or Lip Flip": { en: "PROMO: 11's + Brow Lift or Lip Flip", fr: "PROMO: 11's + Lifting Sourcils ou Lip Flip" },
  "PROMO: Summer Refresh 2 — Mini Refresh": { en: "PROMO: Summer Refresh 2 — Mini Refresh", fr: "PROMO: Rafraîchissement Été 2 — Mini" },
  "PROMO: Summer Refresh 4 — Most Popular": { en: "PROMO: Summer Refresh 4 — Most Popular", fr: "PROMO: Rafraîchissement Été 4 — Le Plus Populaire" },
  "PROMO: Summer Refresh 5 — The Total Face": { en: "PROMO: Summer Refresh 5 — The Total Face", fr: "PROMO: Rafraîchissement Été 5 — Le Visage Complet" },
  
  // Filler
  "The Luscious Lip Enhancement - Full Syringe": { en: "The Luscious Lip Enhancement - Full Syringe", fr: "Augmentation Lèvres Pulpeuses - Seringue Complète" },
  "The Radiant Lip Enhancement - 1/2 Syringe": { en: "The Radiant Lip Enhancement - 1/2 Syringe", fr: "Augmentation Lèvres Radieuses - Demi Seringue" },
  "1 Full Syringe Filler Any Area": { en: "1 Full Syringe Filler Any Area", fr: "1 Seringue Complète Toute Zone" },
  "Filler 2 Syringes + FREE 11's or Forehead Tox": { en: "Filler 2 Syringes + FREE 11's or Forehead Tox", fr: "2 Seringues + Tox 11's ou Front GRATUIT" },
  "3 Syringes of Filler + FREE Tox (Choose One)": { en: "3 Syringes of Filler + FREE Tox (Choose One)", fr: "3 Seringues + Tox GRATUIT (Au Choix)" },
  "Filler 4 Syringes": { en: "Filler 4 Syringes", fr: "Filler 4 Seringues" },
  "Filler 5 Full Syringes": { en: "Filler 5 Full Syringes", fr: "Filler 5 Seringues Complètes" },
  "Filler 6 Syringes (Best Pricing)": { en: "Filler 6 Syringes (Best Pricing)", fr: "Filler 6 Seringues (Meilleur Prix)" },
  "Filler 8 Syringes": { en: "Filler 8 Syringes", fr: "Filler 8 Seringues" },
  "Filler 10 Syringes (Bulk Deal)": { en: "Filler 10 Syringes (Bulk Deal)", fr: "Filler 10 Seringues (Forfait)" },
  "Smokers Lines - 2 Syringes + 16 Units Neurotoxin": { en: "Smokers Lines - 2 Syringes + 16 Units Neurotoxin", fr: "Rides du Fumeur - 2 Seringues + 16 Unités" },
  
  // Sculptra
  "Sculptra Bio Stimulator 1 Vial": { en: "Sculptra Bio Stimulator 1 Vial", fr: "Sculptra Bio-Stimulateur 1 Flacon" },
  "Sculptra Bio Stimulator 2 Vials": { en: "Sculptra Bio Stimulator 2 Vials", fr: "Sculptra Bio-Stimulateur 2 Flacons" },
  "Sculptra Bio Stimulator 4 Vials": { en: "Sculptra Bio Stimulator 4 Vials", fr: "Sculptra Bio-Stimulateur 4 Flacons" },
  "Sculptra + Neuromodulator Full Upper Face Promo": { en: "Sculptra + Neuromodulator Full Upper Face Promo", fr: "Sculptra + Neuromodulateur Promo Visage Supérieur" },
  
  // Medical Micro Needling
  "Medical Micro Needling - 1 Treatment": { en: "Medical Micro Needling - 1 Treatment", fr: "Micro-Aiguilles Médicales - 1 Traitement" },
  "Medical Micro Needling - 2 Treatments": { en: "Medical Micro Needling - 2 Treatments", fr: "Micro-Aiguilles Médicales - 2 Traitements" },
  "Medical Micro Needling - 3 Treatment Package": { en: "Medical Micro Needling - 3 Treatment Package", fr: "Micro-Aiguilles Médicales - Forfait 3 Traitements" },
  "Medical Micro Needling + PRP": { en: "Medical Micro Needling + PRP", fr: "Micro-Aiguilles Médicales + PRP" },
  "Medical Micro Needling + Exosomes": { en: "Medical Micro Needling + Exosomes", fr: "Micro-Aiguilles Médicales + Exosomes" },
  "Salmon Sperm Micro Needling": { en: "Salmon Sperm Micro Needling", fr: "Micro-Aiguilles Sperme de Saumon" },
  
  // PRP Therapy
  "PRP (Platelet Rich Plasma)": { en: "PRP (Platelet Rich Plasma)", fr: "PRP (Plasma Riche en Plaquettes)" },
  "PRP 3 Treatment Promo": { en: "PRP 3 Treatment Promo", fr: "PRP Promo 3 Traitements" },
  "PRP Hair Loss - 4 Treatment Therapy": { en: "PRP Hair Loss - 4 Treatment Therapy", fr: "PRP Perte de Cheveux - Thérapie 4 Traitements" },
  "PRP + Medical Micro Needling (Vampire Facelift)": { en: "PRP + Medical Micro Needling (Vampire Facelift)", fr: "PRP + Micro-Aiguilles (Lifting Vampire)" },
  
  // Products
  "Latisse Eye Lash Serum 3 ML": { en: "Latisse Eye Lash Serum 3 ML", fr: "Sérum Cils Latisse 3 ML" },
  "Latisse Eye Lash Serum 5 ML": { en: "Latisse Eye Lash Serum 5 ML", fr: "Sérum Cils Latisse 5 ML" },
  
  // Consultation
  "Free Consultation Appointment": { en: "Free Consultation Appointment", fr: "Consultation Gratuite" },
  "Follow Up Appointment": { en: "Follow Up Appointment", fr: "Rendez-vous de Suivi" },
};

// Translation maps for service descriptions
const serviceDescriptionTranslations = {
  // Neuromodulator descriptions
  "Complete upper face treatment targeting crow's feet, frown lines (11's), and forehead wrinkles.": { 
    en: "Complete upper face treatment targeting crow's feet, frown lines (11's), and forehead wrinkles.", 
    fr: "Traitement complet du visage supérieur ciblant les pattes d'oie, rides du lion (11's) et rides du front." 
  },
  "Targeted treatment for forehead lines and wrinkles.": { 
    en: "Targeted treatment for forehead lines and wrinkles.", 
    fr: "Traitement ciblé pour les rides et lignes du front." 
  },
  "Treatment for frown lines between the eyebrows.": { 
    en: "Treatment for frown lines between the eyebrows.", 
    fr: "Traitement des rides du lion entre les sourcils." 
  },
  "Special promo! Forehead and frown lines (11's) treatment with unlimited units used for optimal results.": { 
    en: "Special promo! Forehead and frown lines (11's) treatment with unlimited units used for optimal results.", 
    fr: "Promo spéciale! Traitement front et rides du lion (11's) avec unités illimitées pour des résultats optimaux." 
  },
  "Therapeutic Botox treatment to reduce chronic migraine frequency and severity.": { 
    en: "Therapeutic Botox treatment to reduce chronic migraine frequency and severity.", 
    fr: "Traitement thérapeutique au Botox pour réduire la fréquence et la sévérité des migraines chroniques." 
  },
  "Relief for jaw tension, teeth grinding, and TMJ pain. Minimum 65-75 units.": { 
    en: "Relief for jaw tension, teeth grinding, and TMJ pain. Minimum 65-75 units.", 
    fr: "Soulagement des tensions de la mâchoire, grincement des dents et douleur ATM. Minimum 65-75 unités." 
  },
  "Botox treatment to control excessive sweating.": { 
    en: "Botox treatment to control excessive sweating.", 
    fr: "Traitement au Botox pour contrôler la transpiration excessive." 
  },
  "Treatment for visible neck bands.": { 
    en: "Treatment for visible neck bands.", 
    fr: "Traitement des bandes visibles du cou." 
  },
  "Botox treatment for jaw definition and neck lifting.": { 
    en: "Botox treatment for jaw definition and neck lifting.", 
    fr: "Traitement au Botox pour définition de la mâchoire et lifting du cou." 
  },
  "Subtle lift for the corners of the mouth.": { 
    en: "Subtle lift for the corners of the mouth.", 
    fr: "Lifting subtil des coins de la bouche." 
  },
  "Reduce excessive gum display when smiling.": { 
    en: "Reduce excessive gum display when smiling.", 
    fr: "Réduire l'exposition excessive des gencives lors du sourire." 
  },
  "Neuromodulator treatment for a subtle lip enhancement.": { 
    en: "Neuromodulator treatment for a subtle lip enhancement.", 
    fr: "Traitement neuromodulateur pour une augmentation subtile des lèvres." 
  },
  "Treatment for under-eye puffiness.": { 
    en: "Treatment for under-eye puffiness.", 
    fr: "Traitement des poches sous les yeux." 
  },
  "Micro-dosing technique for neck rejuvenation.": { 
    en: "Micro-dosing technique for neck rejuvenation.", 
    fr: "Technique de micro-dosage pour le rajeunissement du cou." 
  },
  "Complete micro-dosing treatment for face and neck.": { 
    en: "Complete micro-dosing treatment for face and neck.", 
    fr: "Traitement complet de micro-dosage pour le visage et le cou." 
  },
  "Botox treatment for the trapezoid muscles to slim the neck-shoulder area and relieve tension.": {
    en: "Botox treatment for the trapezoid muscles to slim the neck-shoulder area and relieve tension.",
    fr: "Traitement Botox pour les muscles trapèzes pour affiner la zone cou-épaules et soulager les tensions."
  },
  "Subtle lift to open and define the brow area.": {
    en: "Subtle lift to open and define the brow area.",
    fr: "Lifting subtil pour ouvrir et définir la zone des sourcils."
  },
  "Treatment for fine lines on the sides of the nose when scrunching.": {
    en: "Treatment for fine lines on the sides of the nose when scrunching.",
    fr: "Traitement des ridules sur les côtés du nez lors du plissement."
  },
  "Complete upper face treatment covering 7 zones: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, and Lip Flip.": {
    en: "Complete upper face treatment covering 7 zones: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, and Lip Flip.",
    fr: "Traitement complet du visage supérieur couvrant 7 zones: Front, Pattes d'oie, 11's, Lifting des sourcils, Smile Lift, Rides du nez et Lip Flip."
  },
  "Summer promo! 11's treatment with your choice of Brow Lift or Lip Flip. May substitute 11's for Forehead.": {
    en: "Summer promo! 11's treatment with your choice of Brow Lift or Lip Flip. May substitute 11's for Forehead.",
    fr: "Promo été! Traitement des 11's avec choix de Lifting Sourcils ou Lip Flip. Peut substituer les 11's par le Front."
  },
  "Spring promo! Includes Crow's Feet, Forehead, and Brow Lift. Lip Flip may substitute Brow Lift.": {
    en: "Spring promo! Includes Crow's Feet, Forehead, and Brow Lift. Lip Flip may substitute Brow Lift.",
    fr: "Promo printemps! Inclut Pattes d'oie, Front et Lifting Sourcils. Lip Flip peut remplacer le Lifting."
  },
  "Spring promo! 7 zones: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, and Lip Flip.": {
    en: "Spring promo! 7 zones: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, and Lip Flip.",
    fr: "Promo printemps! 7 zones: Front, Pattes d'oie, 11's, Lifting Sourcils, Smile Lift, Rides du nez et Lip Flip."
  },
  "Spring promo! Everything in Refresh 4 PLUS Neck Bands: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, Lip Flip, and Neck Bands.": {
    en: "Spring promo! Everything in Refresh 4 PLUS Neck Bands: Forehead, Crow's Feet, 11's, Brow Lift, Smile Lift, Bunny Lines, Lip Flip, and Neck Bands.",
    fr: "Promo printemps! Tout le Refresh 4 PLUS Bandes du cou: Front, Pattes d'oie, 11's, Lifting Sourcils, Smile Lift, Rides du nez, Lip Flip et Bandes du cou."
  },
  
  // Filler descriptions
  "Full syringe lip filler for beautiful, natural-looking results.": { 
    en: "Full syringe lip filler for beautiful, natural-looking results.", 
    fr: "Seringue complète de filler pour des lèvres naturelles et magnifiques." 
  },
  "Half syringe lip filler for subtle enhancement.": { 
    en: "Half syringe lip filler for subtle enhancement.", 
    fr: "Demi-seringue de filler pour une augmentation subtile." 
  },
  "One full syringe of premium dermal filler for any treatment area.": { 
    en: "One full syringe of premium dermal filler for any treatment area.", 
    fr: "Une seringue complète de filler premium pour toute zone de traitement." 
  },
  "Two syringes of filler with complimentary neuromodulator treatment.": { 
    en: "Two syringes of filler with complimentary neuromodulator treatment.", 
    fr: "Deux seringues de filler avec traitement neuromodulateur offert." 
  },
  "Three syringes of premium dermal filler + either FREE Crow's Feet or 11's or Forehead. Choose one.": { 
    en: "Three syringes of premium dermal filler + either FREE Crow's Feet or 11's or Forehead. Choose one.", 
    fr: "Trois seringues de filler premium + Pattes d'oie OU 11's OU Front GRATUIT. Au choix." 
  },
  "Four syringes of premium dermal filler.": { 
    en: "Four syringes of premium dermal filler.", 
    fr: "Quatre seringues de filler premium." 
  },
  "Five syringes for comprehensive facial rejuvenation.": { 
    en: "Five syringes for comprehensive facial rejuvenation.", 
    fr: "Cinq seringues pour un rajeunissement facial complet." 
  },
  "Six syringes - our best value option for full facial rejuvenation.": { 
    en: "Six syringes - our best value option for full facial rejuvenation.", 
    fr: "Six seringues - notre meilleur rapport qualité-prix pour un rajeunissement facial complet." 
  },
  "Eight syringes for extensive facial enhancement.": { 
    en: "Eight syringes for extensive facial enhancement.", 
    fr: "Huit seringues pour une amélioration faciale complète." 
  },
  "Premium bulk package for comprehensive treatment.": { 
    en: "Premium bulk package for comprehensive treatment.", 
    fr: "Forfait premium pour un traitement complet." 
  },
  "Targeted treatment for lip lines with filler and neuromodulator.": { 
    en: "Targeted treatment for lip lines with filler and neuromodulator.", 
    fr: "Traitement ciblé des rides des lèvres avec filler et neuromodulateur." 
  },
  
  // Sculptra descriptions
  "Single vial Sculptra treatment for natural collagen stimulation.": { 
    en: "Single vial Sculptra treatment for natural collagen stimulation.", 
    fr: "Traitement Sculptra un flacon pour stimulation naturelle du collagène." 
  },
  "Two vial treatment for enhanced collagen building.": { 
    en: "Two vial treatment for enhanced collagen building.", 
    fr: "Traitement deux flacons pour une production accrue de collagène." 
  },
  "Comprehensive four vial treatment for maximum results.": { 
    en: "Comprehensive four vial treatment for maximum results.", 
    fr: "Traitement complet quatre flacons pour des résultats maximaux." 
  },
  "Combination treatment with Sculptra and full upper face Botox.": { 
    en: "Combination treatment with Sculptra and full upper face Botox.", 
    fr: "Traitement combiné Sculptra et Botox visage supérieur complet." 
  },
  
  // Micro Needling descriptions
  "Single treatment at 0.3mm or deeper depth, performed by RN.": { 
    en: "Single treatment at 0.3mm or deeper depth, performed by RN.", 
    fr: "Traitement unique à 0.3mm ou plus profond, effectué par infirmière." 
  },
  "Two treatment package for enhanced results.": { 
    en: "Two treatment package for enhanced results.", 
    fr: "Forfait deux traitements pour des résultats améliorés." 
  },
  "Three treatment package for optimal skin rejuvenation.": { 
    en: "Three treatment package for optimal skin rejuvenation.", 
    fr: "Forfait trois traitements pour un rajeunissement optimal de la peau." 
  },
  "Micro needling enhanced with Platelet Rich Plasma.": { 
    en: "Micro needling enhanced with Platelet Rich Plasma.", 
    fr: "Micro-aiguilles améliorées avec Plasma Riche en Plaquettes." 
  },
  "Advanced micro needling with exosome therapy.": { 
    en: "Advanced micro needling with exosome therapy.", 
    fr: "Micro-aiguilles avancées avec thérapie aux exosomes." 
  },
  "Salmon Sperm plus Micro Needling.": { 
    en: "Salmon Sperm plus Micro Needling.", 
    fr: "Sperme de Saumon plus Micro-Aiguilles." 
  },
  
  // PRP descriptions
  "Single PRP treatment for skin or hair rejuvenation.": { 
    en: "Single PRP treatment for skin or hair rejuvenation.", 
    fr: "Traitement PRP unique pour rajeunissement peau ou cheveux." 
  },
  "Three treatment package for face, hair, or neck.": { 
    en: "Three treatment package for face, hair, or neck.", 
    fr: "Forfait trois traitements pour visage, cheveux ou cou." 
  },
  "Complete hair restoration program with four PRP sessions.": { 
    en: "Complete hair restoration program with four PRP sessions.", 
    fr: "Programme complet de restauration capillaire avec quatre séances PRP." 
  },
  "Combined PRP and micro needling for ultimate skin rejuvenation.": { 
    en: "Combined PRP and micro needling for ultimate skin rejuvenation.", 
    fr: "PRP et micro-aiguilles combinés pour un rajeunissement cutané ultime." 
  },
  
  // Products descriptions
  "FDA-approved eyelash growth serum - 3ml bottle.": { 
    en: "FDA-approved eyelash growth serum - 3ml bottle.", 
    fr: "Sérum de croissance des cils approuvé FDA - flacon 3ml." 
  },
  "FDA-approved eyelash growth serum - 5ml bottle.": { 
    en: "FDA-approved eyelash growth serum - 5ml bottle.", 
    fr: "Sérum de croissance des cils approuvé FDA - flacon 5ml." 
  },
  
  // Consultation descriptions
  "Complimentary consultation to discuss your aesthetic goals.": { 
    en: "Complimentary consultation to discuss your aesthetic goals.", 
    fr: "Consultation gratuite pour discuter de vos objectifs esthétiques." 
  },
  "Complimentary follow-up to assess your results.": { 
    en: "Complimentary follow-up to assess your results.", 
    fr: "Suivi gratuit pour évaluer vos résultats." 
  },
};

const ServiceCard = ({ service, t, language }) => {
  const { addToCart, isInCart, removeFromCart } = useCart();
  const inCart = isInCart(service.id);

  const handleClick = () => {
    if (inCart) {
      removeFromCart(service.id);
    } else {
      addToCart(service);
    }
  };

  // Get translated name and description
  const translatedName = serviceNameTranslations[service.name]?.[language] || service.name;
  const translatedDescription = serviceDescriptionTranslations[service.description]?.[language] || service.description;

  // Calculate Afterpay installment (price / 4)
  const priceNum = parseFloat(service.price?.replace(/[^0-9.]/g, '') || '0');
  const afterpayInstallment = priceNum >= 35 ? (priceNum / 4).toFixed(2) : null;

  return (
    <div
      className={`service-card-glass rounded-xl p-5 cursor-pointer ${
        inCart ? "ring-2 ring-primary/30 bg-primary/5" : ""
      }`}
      onClick={handleClick}
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-semibold text-base leading-tight pr-2">{translatedName}</h4>
        <span className="text-primary font-bold text-lg whitespace-nowrap price-pop">{service.price}</span>
      </div>
      <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{translatedDescription}</p>
      
      {/* Afterpay Badge - More Prominent */}
      {afterpayInstallment && (
        <div className="flex items-center gap-1.5 mb-3 px-2 py-1.5 rounded-lg bg-gradient-to-r from-[#b2fce4]/30 to-[#e0fcf4]/30 border border-[#b2fce4]/50">
          <span className="text-[10px] md:text-xs text-gray-600">
            {language === 'fr' ? 'ou' : 'or'}
          </span>
          <span className="font-bold text-xs md:text-sm text-teal-700">${afterpayInstallment}</span>
          <span className="text-[10px] md:text-xs text-gray-600">× 4</span>
          <span className="bg-black text-[#b2fce4] text-[9px] md:text-[10px] font-bold px-1.5 py-0.5 rounded ml-1">
            afterpay
          </span>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        {service.duration && service.duration !== "0 mins" && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{service.duration}</span>
          </div>
        )}
        <button
          className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-300 btn-magnetic ${
            inCart
              ? "bg-primary text-white"
              : "bg-primary/10 text-primary hover:bg-primary/20"
          }`}
          data-testid={`add-to-cart-${service.id}`}
        >
          {inCart ? (
            <>
              <Check className="w-3 h-3" />
              {t("services.added")}
            </>
          ) : (
            <>
              <Plus className="w-3 h-3" />
              {t("services.add")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const CategorySection = ({ category, services, isOpen, onToggle, t, language }) => {
  const Icon = categoryIcons[category] || Sparkles;
  const translatedCategory = categoryTranslations[category]?.[language] || category;
  
  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-card border border-border rounded-xl hover:bg-muted/50 transition-colors"
        data-testid={`category-toggle-${category.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              {translatedCategory}
            </h3>
            <span className="text-sm text-muted-foreground">{services.length} {t("services.servicesCount")}</span>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      
      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 animate-fade-in">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} t={t} language={language} />
          ))}
        </div>
      )}
    </div>
  );
};

const ServicesSection = ({ services }) => {
  const { t, language } = useLanguage();
  const [openCategories, setOpenCategories] = useState(["Neuromodulator - Botox", "Filler"]);
  
  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    const category = service.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});

  // Define category order
  const categoryOrder = [
    "Neuromodulator - Botox",
    "Filler",
    "Sculptra",
    "Medical Micro Needling",
    "PRP Therapy",
    "Products",
    "Consultation"
  ];

  const toggleCategory = (category) => {
    setOpenCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const expandAll = () => {
    setOpenCategories(categoryOrder);
  };

  const collapseAll = () => {
    setOpenCategories([]);
  };

  return (
    <section
      id="services"
      className="section-padding bg-muted"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="font-mono-label text-xs text-primary tracking-widest">
            {t("services.title")}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("services.heading")} <span className="italic text-primary">{t("services.headingAccent")}</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("services.description")}
          </p>
        </div>

        {/* Featured Image Gallery */}
        <div className="mb-12">
          {/* 4 Photos Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 rounded-t-2xl overflow-hidden">
            <div className="aspect-[3/4] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/g0yy4wv3_k1.jpg"
                alt="Karine Lauzon RN"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/zh3piaee_k3.jpg"
                alt="Karine Lauzon RN"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/7hse4fvj_k19.jpg"
                alt="Karine Lauzon RN"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_85a700af-9f96-42a1-b28b-49447854ad83/artifacts/tpudq85l_k223.jpg"
                alt="Karine Lauzon RN"
                className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
          
          {/* Location Bar */}
          <div className="bg-zinc-900 text-white py-4 px-6 rounded-b-2xl">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 text-center">
              <div>
                <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">Ottawa</p>
                <p className="text-sm text-gray-300">1390 Prince of Wales Drive, Main Floor</p>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-600"></div>
              <div>
                <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">Carleton Place</p>
                <p className="text-sm text-gray-300">163 Bridge Street, Downtown</p>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-600"></div>
              <div>
                <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">Kanata</p>
                <p className="text-sm text-gray-300">320 March Road, Sixth Floor, Clinic 603</p>
              </div>
            </div>
          </div>
        </div>

        {/* Oct 21 2026 · live Full Menu banner — counts come straight from the
            Square catalog of all 3 clinics (replaces the stale hard-coded
            "30 packages / 3 Botox" card). */}
        <FullMenuBanner isFr={language === "fr"} />

        {/* Service Category Navigation Boxes */}
        <div className="mb-10" data-testid="service-category-nav">
          <p className="text-center text-sm text-muted-foreground mb-5 uppercase tracking-widest" style={{ fontFamily: "'Playfair Display', serif" }}>
            {language === "fr" ? "Explorer nos traitements" : "Explore Our Treatments"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {TREATMENT_CATEGORY_CARDS.map((svc) => (
              <TreatmentCategoryCard key={svc.name} svc={svc} isFr={language === "fr"} testIdPrefix="service-nav" />
            ))}
          </div>
        </div>

        {/* Expand/Collapse Controls */}
        <div className="flex justify-end gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all">
            {t("services.expandAll")}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all">
            {t("services.collapseAll")}
          </Button>
        </div>

        {/* Service Categories */}
        <div className="space-y-4">
          {categoryOrder.map((category) => {
            const categoryServices = groupedServices[category];
            if (!categoryServices || categoryServices.length === 0) return null;
            
            return (
              <CategorySection
                key={category}
                category={category}
                services={categoryServices}
                isOpen={openCategories.includes(category)}
                onToggle={() => toggleCategory(category)}
                t={t}
                language={language}
              />
            );
          })}
        </div>

        {/* Pricing Note */}
        <div className="mt-12 p-6 bg-card border border-border rounded-xl text-center">
          <p className="text-muted-foreground text-sm">
            <strong>Note:</strong> {t("services.pricingNote")} <span className="text-primary font-medium">{t("services.freeConsultation")}</span> {t("services.toDiscuss")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
